/**
 * The two small readouts of f8's memory state (PPL-350).
 *
 * `SlotPill` — the account's remaining slots. `RememberStatusPill` — one
 * person's state beside their name. `RememberMarker` — the same state as a bare
 * glyph, for list rows.
 *
 * All three render NOTHING in their "no answer" case, and that is the point:
 *
 *   * premium has no slot counter. A limit you cannot reach is not reassurance,
 *     it is a standing reminder that a limit exists, on the one plan without one.
 *   * a never-remembered person carries no chip and no marker — not a greyed one,
 *     not an outline. Most of a synced address book lives in that state, and
 *     marking it would turn the marker into wallpaper: the sparkle only means
 *     something because most rows do not have one.
 */

import React from 'react';
import { Moon, Sparkle } from './glyphs';
import { RT } from './tokens';
import { slotLabel, type RememberQuota, type RememberState } from '../../services/rememberService';

export function SlotPill({
  quota,
  small = false,
}: {
  quota: Pick<RememberQuota, 'isPremium' | 'limit' | 'activeCount'> | null;
  small?: boolean;
}) {
  if (!quota) return null;
  const label = slotLabel(quota);
  if (!label) return null;

  const full = quota.limit !== null && quota.limit - quota.activeCount <= 0;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
        padding: small ? '3px 9px' : '5px 11px', borderRadius: 999,
        // Gold at zero, never red: being at the limit is a state to act on, not
        // an error to apologise for — and it is the most valuable moment there is.
        background: full ? RT.goldTint : RT.surface,
        border: `1px solid ${full ? RT.goldTintBorder : RT.border}`,
        fontSize: small ? 10.5 : 11.5, fontWeight: 600,
        color: full ? RT.gold : RT.muted,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <Sparkle size={small ? 9 : 10} color={full ? RT.gold : RT.dimSolid} />
      {label}
    </span>
  );
}

export function RememberStatusPill({ state }: { state: RememberState }) {
  if (state === 'never') return null;
  const active = state === 'active';
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 9px', borderRadius: 999,
        background: active ? RT.magentaTint : RT.goldTint,
        border: `1px solid ${active ? RT.magentaTintBorder : RT.goldTintBorder}`,
        fontSize: 10.5, fontWeight: 600,
        color: active ? RT.magenta : RT.gold,
      }}
    >
      {active ? <Sparkle size={9} color={RT.magenta} /> : <Moon size={9} color={RT.gold} />}
      {/* "f8 remembers", never "track" — what is sold is memory, so what is
          metered is memory, and tracking is surveillance vocabulary. */}
      {active ? 'f8 remembers' : 'paused'}
    </span>
  );
}

export function RememberMarker({ state, size = 15 }: { state: RememberState; size?: number }) {
  if (state === 'active') return <Sparkle size={size} color={RT.magenta2} />;
  if (state === 'paused') return <Moon size={size} color="rgba(252,211,77,0.75)" />;
  return null;
}
